import React, { useMemo } from 'react';
import { Form, Row, Col, Tag } from '@bedrock/components';
import SFormItem from '#components/SForm/SFormItem';
import styles from './index.less';
import classNames from 'classnames/bind';
import { currencyEnum, localPx2rem } from '#utils';
import { useChangeRate } from '#hooks/useChangeRate';
import { CurrencyEnum } from '#services/api';

const cx = classNames.bind(styles);

interface OptionItem {
  name: string;
  value: any;
}

export type ViewCurrency = 'original' | CurrencyEnum;

export enum SupplierFilterEnum {
  LOWEST = 'lowest',
  SHOW_ALL = 'show_all',
}

interface FilterBarProps {
  /** 当前轮次 */
  currentRound?: number;
  /** 轮次变化回调 */
  onRoundChange?: (round: number) => void;
  /** 币种选择 */
  viewCurrency?: ViewCurrency;
  /** 币种变化回调 */
  onViewCurrencyChange?: (currency: ViewCurrency) => void;
  /** 供应商筛选 */
  supplierFilter?: string;
  /** 供应商筛选变化回调 */
  onSupplierFilterChange?: (filter: string) => void;
  /** 是否显示供应商筛选 */
  showSupplierFilter?: boolean;
  /** 右侧插槽 */
  rightSlot?: React.ReactNode;
  /** 轮次选项 */
  roundOptions?: OptionItem[];
}

/**
 * 头部筛选栏组件
 * 包含供应商、币种、轮次等筛选条件
 */
const FilterBar: React.FC<FilterBarProps> = ({
  currentRound = 3,
  onRoundChange,
  viewCurrency = 'original',
  onViewCurrencyChange,
  supplierFilter = SupplierFilterEnum.LOWEST,
  onSupplierFilterChange,
  rightSlot,
  roundOptions = [],
  showSupplierFilter = false,
}) => {
  const [form] = Form.useForm();
  const { changeRateList } = useChangeRate();

  // 币种选项
  const viewCurrencyOptions: OptionItem[] = useMemo(() => {
    const currencyList = Array.from(
      new Set(changeRateList.map((item) => item.from_currency))
    );
    const res = currencyList.map((item) => {
      return {
        name: currencyEnum[item?.toLowerCase()],
        value: item,
      };
    });
    return [
      {
        name: '原币种',
        value: 'original',
      },
      ...res,
    ];
  }, [changeRateList]);

  // 初始化表单值
  React.useEffect(() => {
    form.setFieldsValue({
      supplierFilter,
      viewCurrency,
      currentRound,
    });
  }, [supplierFilter, viewCurrency, currentRound, form]);

  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.supplierFilter !== undefined) {
      onSupplierFilterChange?.(changedValues.supplierFilter);
    }
    if (changedValues.viewCurrency !== undefined) {
      onViewCurrencyChange?.(changedValues.viewCurrency);
    }
    if (changedValues.currentRound !== undefined) {
      onRoundChange?.(changedValues.currentRound);
    }
  };

  return (
    <Form
      form={form}
      onValuesChange={handleValuesChange}
      layout="inline"
      showLabelColon
      className={cx('filter-bar-form')}
    >
      {showSupplierFilter && (
        <SFormItem
          formItemKey="supplierFilter"
          componentClass="select"
          label={'供应商'}
          elementProps={{
            size: 'small',
            options: [
              { name: '仅展示最低价', value: SupplierFilterEnum.LOWEST },
              { name: '展示所有供应商', value: SupplierFilterEnum.SHOW_ALL },
            ],
            showSearch: false,
            allowClear: false,
            dropdownStyle: {
              width: localPx2rem(150),
            },
          }}
        />
      )}

      <SFormItem
        formItemKey="viewCurrency"
        componentClass="select"
        label={'币种'}
        elementProps={{
          size: 'small',
          options: viewCurrencyOptions,
          showSearch: false,
          allowClear: false,
          dropdownStyle: {
            width: localPx2rem(120),
          },
        }}
      />

      <div className={'flex items-center gap-[8px]'}>
        <SFormItem
          formItemKey="currentRound"
          componentClass="select"
          label={'轮次'}
          elementProps={{
            size: 'small',
            options: roundOptions,
            showSearch: false,
            allowClear: false,
            dropdownStyle: {
              width: localPx2rem(80),
            },
            extra: (
              <Tag color="light-blue" size="xSmall" text={`当前轮次`}></Tag>
            ),
          }}
        />
        {currentRound === roundOptions?.[0]?.value && (
          <Tag color="light-blue" size="xSmall" text={`当前轮次`}></Tag>
        )}
      </div>
      {rightSlot}
    </Form>
  );
};

export default FilterBar;
